import type { Money } from '@dastarhan/contracts';

/**
 * Обслуживание, перенос и права в зале.
 *
 * Три вещи, без которых ресторан не работает, а кафе с одним столом
 * обходится. Все три пришли из разбора живой работы, а не из головы.
 */

// ─────────────────────────────────────────── сервисный сбор

export type ServiceCharge = {
  /** процент к сумме заказа: 10 = «плюс 10%» */
  percent: number;
  /** только для заказов в зале — на вынос сбор не берут */
  dineInOnly: boolean;
  /** от скольких гостей включается; 0 — всегда */
  fromGuests: number;
};

export const NO_SERVICE: ServiceCharge = { percent: 0, dineInOnly: true, fromGuests: 0 };

/**
 * Берётся ли сбор с этого заказа. Правило простое, но проверять
 * его надо в одном месте: иначе касса, чек и отчёт разойдутся.
 */
export function serviceApplies(
  cfg: ServiceCharge,
  mode: 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY',
  guests: number,
): boolean {
  if (cfg.percent <= 0) return false;
  if (cfg.dineInOnly && mode !== 'DINE_IN') return false;
  return guests >= cfg.fromGuests;
}

/**
 * Сумма сбора. Считается от суммы ПОСЛЕ скидки: сбор берут
 * с того, что гость реально платит, а не с зачёркнутой цены.
 */
export function serviceAmount(subtotalAfterDiscount: Money, cfg: ServiceCharge): Money {
  if (cfg.percent <= 0 || subtotalAfterDiscount <= 0) return 0;
  return Math.round((subtotalAfterDiscount * cfg.percent) / 100);
}

// ─────────────────────────────────────────── перенос

export type TransferItem = { itemId: string; name: string; qty: number; sum: Money };

/**
 * Что можно перенести на другой стол. Переносим только то,
 * что ещё не оплачено и не снято: оплаченная позиция принадлежит
 * закрытому чеку, а снятая уже объяснена менеджером.
 */
export function transferable<T extends { itemId: string; removed?: boolean; paid?: boolean }>(
  items: T[],
): T[] {
  return items.filter((i) => !i.removed && !i.paid);
}

/**
 * Можно ли переносить на этот стол. Занятый стол — не помеха:
 * позиции просто добавятся к открытому заказу. А вот на тот же
 * стол переносить бессмысленно, и это стоит сказать вслух.
 */
export function canTransferTo(
  fromTableId: string | null,
  toTableId: string,
): { ok: true } | { ok: false; reason: string } {
  if (!fromTableId) return { ok: false, reason: 'Заказ ещё не привязан к столу' };
  if (fromTableId === toTableId) return { ok: false, reason: 'Это тот же самый стол' };
  return { ok: true };
}

// ─────────────────────────────────────────── чей стол

/**
 * Видит ли этот сотрудник чужой стол.
 *
 * Официант отвечает за свои столы и не должен путаться в чужих —
 * но и запирать наглухо нельзя: смены пересекаются, официант уходит
 * домой, а гость остаётся. Поэтому чужие столы видны, но помечены,
 * а войти в них может старший.
 */
export function tableAccess(
  openedById: string | null,
  meId: string,
  meIsManager: boolean,
): 'own' | 'foreign-allowed' | 'foreign-locked' {
  if (openedById === null || openedById === meId) return 'own';
  return meIsManager ? 'foreign-allowed' : 'foreign-locked';
}

// ─────────────────────────────────────────── поиск блюда

/**
 * Поиск по меню. Официант помнит название неточно и печатает
 * на бегу, поэтому ищем по вхождению в любом месте слова
 * и не мучаем регистром.
 */
export function matchProduct(name: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return name.toLowerCase().includes(q);
}

/**
 * Порядок выдачи: сначала те, что начинаются с запроса.
 * «Ман» должен показать «Манты» раньше, чем «Лагман».
 */
export function rankProducts<T extends { name: string }>(items: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  const found = items.filter((i) => matchProduct(i.name, q));
  return found.sort((a, b) => {
    const aStarts = a.name.toLowerCase().startsWith(q) ? 0 : 1;
    const bStarts = b.name.toLowerCase().startsWith(q) ? 0 : 1;
    return aStarts - bStarts || a.name.localeCompare(b.name, 'ru');
  });
}
